import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { company, customerData } from '../utilities/CustomerData'
import { apiURL } from '../utilities/URLs'
import posthog from 'posthog-js'

import '../css/loader.css'
import { getCompanyURL } from '../utilities/extensions'
import SignUpModal from './SignUpModal'

export default function Login({ setLoggedIn, setShowNavBar }) {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const urlEmail = urlSearchParams.get('email') ?? ''
  const urlLast4SSN = urlSearchParams.get('last4SSN') ?? ''
  const urlPassword = urlSearchParams.get('password') ?? ''

  const idMeURL = `https://api.idmelabs.com/oauth/authorize?client_id=${process.env.REACT_APP_IDME_CLIENT_ID}&redirect_uri=${apiURL}/idme&response_type=code&scope=login&state=login`

  const navigate = useNavigate()
  const location = useLocation()
  const expired = location.state ? location.state.expired : false
  const idmeNotFound = location.state ? location.state.idme : false

  const [email, setEmail] = useState(urlEmail)
  const [password, setPassword] = useState(urlPassword)
  const [loginStatus, setLoginStatus] = useState('')
  const [error, setError] = useState('')

  const [signUpEmail, setSignUpEmail] = useState(urlEmail)
  const [signUpLast4SSN, setSignUpLast4SSN] = useState(urlLast4SSN)
  const [signUpError, setSignUpError] = useState('')
  const [signUpLoading, setSignUpLoading] = useState('')

  const [activeTab, setActiveTab] = useState('login')
  const [showSignUpModal, setShowSignUpModal] = useState(false)

  const handleLogin = async () => {
    if (email.length > 0 && password.length > 0) {
      setLoginStatus('Logging In...')
      const form = new FormData()
      form.append('loginInfo', JSON.stringify({
        companyId: company.companyId,
        email: email.trim().toLocaleLowerCase(),
        password: password.trim()
      }))
      const loginResponse = await fetch(`${apiURL}/api/login`, {
        method: 'POST',
        body: form,
        credentials: 'include'
      })
      const loginData = await loginResponse.json()

      if (loginData.response.error) {
        if(loginData.response.error === 'Wrong Company')
          window.location.href = `${getCompanyURL(loginData.response.customerCompanyId)}?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`
        else if(loginData.response.error === 'Customer Does Not Exist.') {
          setLoginStatus('')
          setEmail('')
          setPassword('')
          setShowSignUpModal(true)
        } else {
          setLoginStatus('')
          setError(loginData.response.error)
          setTimeout(() => {
            setError('')
          }, 2000)
        }
      } else {
        customerData.setCustomerData(loginData.response.customerAccount)
        customerData.accessToken = loginData.response.accessToken
        localStorage.setItem('customerAccessToken', customerData.accessToken)
        posthog.identify(customerData.customerLeadData.leadContact.contactId, {
          name: customerData.customerLeadData.leadContact.contactName,
          email: customerData.customerLeadData.leadContact.contactEmail
        })
        setLoggedIn(true)
        setShowNavBar(true)
        navigate('/dashboard')
      }
    } else {
      setError('Please Enter your Email/Password')
      setTimeout(() => {
        setError('')
      }, 2000)
    }
  }

  const handleSignUp = async () => {
    if (signUpEmail.length > 0 && signUpLast4SSN.length > 0) {
      setSignUpLoading('Requesting Email...')
      const sendEmailResponse = await fetch(`${apiURL}/api/requestSignUpToken?email=${encodeURIComponent(signUpEmail.trim().toLocaleLowerCase())}&companyId=${encodeURIComponent(company.companyId)}&last4SSN=${encodeURIComponent(signUpLast4SSN)}`)
      const sendEmailData = await sendEmailResponse.json()

      if (sendEmailData.response.error) {
        if(sendEmailData.response.error === 'Wrong Company')
          window.location.href = `${getCompanyURL(sendEmailData.response.customerCompanyId)}?email=${encodeURIComponent(signUpEmail.trim())}&last4SSN=${encodeURIComponent(signUpLast4SSN)}`
        else {
          setSignUpLoading('')
          setSignUpError(sendEmailData.response.error)
        }
      } else
        navigate(`/signUp/${sendEmailData.response.signUpToken}`)
    } else {
      setSignUpError('Please Provide Both Email and SSN')
      setTimeout(() => {
        setSignUpError('')
      }, 2000)
    }
  }

  useEffect(() => {
    if(urlEmail && urlLast4SSN)
      handleSignUp()
    if(urlEmail && urlPassword)
      handleLogin()
  }, [signUpEmail, signUpLast4SSN])

  return (
    <div className='flex flex-col items-center justify-center h-svh text-center font-mont'>
      <img className='w-60 lg:mb-28 max-sm:mt-20 max-sm:mb-10' src={company.companyLogoURL} alt='setcLogo' />
      <div className='p-8 lg:max-w-xl w-10/12 bg-white rounded-lg shadow mb-28 flex flex-col'>
        <div className='flex flex-col lg:flex-row lg:items-start'>
          <div className='lg:hidden flex justify-center mb-6'>
            <button
              className={`px-4 py-2 font-bold ${activeTab === 'login' ? 'bg-primaryColor text-white' : 'bg-gray-200 text-gray-700'} rounded`}
              onClick={() => setActiveTab('login')}
            >
              Login
            </button>
            <button
              className={`px-4 py-2 font-bold ${activeTab === 'signup' ? 'bg-secondaryColor text-white' : 'bg-gray-200 text-gray-700'} rounded`}
              onClick={() => setActiveTab('signup')}
            >
              Sign Up
            </button>
          </div>
          <div className='flex flex-col lg:flex-row'>
            <div className={`flex-1 ${activeTab === 'login' || window.innerWidth >= 1024 ? 'block' : 'hidden'} lg:block`}>
              <p className='font-bold text-gray-500 text-lg'>Login</p>
              <div className='mb-4 lg:mt-[3.85rem]'>
                <label htmlFor='email' className='block mb-2 text-sm font-bold text-gray-700 text-left'>
                  Email
                </label>
                <input
                  type='email'
                  id='email'
                  className='w-full px-3 py-2 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className='mb-6'>
                <label htmlFor='password' className='block mb-2 text-sm font-bold text-gray-700 text-left'>
                  Password
                </label>
                <input
                  type='password'
                  id='password'
                  className='w-full px-3 py-2 mb-3 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className='flex flex-col justify-center gap-4 items-center'>
                <button onClick={() => handleLogin()} className='w-full py-2 font-bold text-white bg-primaryColor rounded hover:bg-primaryDarkColor transition duration-200'>
                  Log In
                </button>
              </div>
              <p className='resetPassword text-xs mt-5 text-center'>Forgot Password? Reset it <span onClick={() => navigate('/requestPasswordReset')} className='text-blue-600 cursor-pointer'>here</span></p>
              {error.length > 0 && (
                <p className='text-red-500 mt-5'>{error}</p>
              )}
              {expired && (
                <p className='text-red-500 mt-5'>Session Expired.</p>
              )}
              {idmeNotFound && (
                <p className='text-red-500 mt-5'>Could not find account with ID.me. Please try the normal login.</p>
              )}
              {loginStatus.length > 0 && (
                <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                  <p>{loginStatus}</p>
                </div>
              )}
            </div>
            <div className='max-sm:hidden flex lg:flex-col flex-row items-center lg:ml-4 lg:mr-4 max-sm:mt-4 max-sm:mb-4'>
              <div className='flex-1 lg:w-px lg:h-full h-px bg-gray-300'></div>
              <p className='px-2 text-gray-400 font-bold'>OR</p>
              <div className='flex-1 lg:w-px lg:h-full h-px bg-gray-300'></div>
            </div>
            <div className={`flex-1 ${activeTab === 'signup' || window.innerWidth >= 1024 ? 'block' : 'hidden'} lg:block`}>
              <p className='font-bold text-gray-500 text-lg'>Sign Up</p>
              <p className='font-light italic text-gray-500 text-xs mt-4 mb-4'>*Please use the same email you used when you applied with {company.companyName}.</p>
              <div className='mb-4'>
                <label htmlFor='signUpEmail' className='block mb-2 text-sm font-bold text-gray-700 text-left'>
                  Email
                </label>
                <input
                  type='email'
                  id='signUpEmail'
                  className='w-full px-3 py-2 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline'
                  value={signUpEmail}
                  onChange={(e) => setSignUpEmail(e.target.value)}
                  required
                />
              </div>
              <div className='mb-4'>
                <label htmlFor='signUpLast4SSN' className='block mb-2 text-sm font-bold text-gray-700 text-left'>
                  Last 4 SSN
                </label>
                <input
                  type='password'
                  id='signUpLast4SSN'
                  className='mb-4 w-full px-3 py-2 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:shadow-outline'
                  value={signUpLast4SSN}
                  onChange={(e) => setSignUpLast4SSN(e.target.value)}
                  required
                />
              </div>
              <div className='justify-center'>
                <button onClick={() => handleSignUp()} className='w-full py-2 font-bold text-white bg-primaryColor rounded hover:bg-primaryDarkColor transition duration-200'>
                  Register
                </button>
              </div>
              {signUpLoading.length > 0 && (
                <p className='mt-5 text-sm'>{signUpLoading}</p>
              )}
              {signUpError.length > 0 && (
                <>
                  {signUpError === 'There Is No Record With This Email.' ? (
                    <>
                      <p className='mt-5 text-sm'>We couldn't find your user information. Please ensure you have completed the SETC Pros application and submitted your Tax Status consent at</p>
                      <a className='underline text-blue-800 text-sm' href='https://setcpros.app.tax/' target='_blank'>{'\nSETC Pros Tax Consent.'}</a>
                    </>
                  ) : (
                    <p className='mt-5 text-sm'>{signUpError}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {showSignUpModal && (
        <SignUpModal />
      )}
    </div>
  )
}

/**
 * <button onClick={() => window.location.href = idMeURL}>
                  <img className='w-56 h-11' src='https://s3.amazonaws.com/idme/developer/idme-buttons/assets/img/signin.svg' />
                </button>
                
 */